import { useUserColumns } from "@/columns/user-manangement";
import {
  DeleteConfirmModal,
  DynamicTable,
  IconImage,
  PageHeader,
  RestrictUserModal,
  UnblockUserModal,
} from "@/components";
import {
  black_filter_img,
  plus_img,
  search_img,
  userRightsEnums,
} from "@/constants";
import { useDebounce, useSnackbar } from "@/hooks";
import {
  useCreateUserMutation,
  useDeleteUserMutation,
  useEditUserMutation,
  useGetAllTdClientsQuery,
  useUpdateUserStatusMutation,
} from "@/services/user-management/user-managment-api";
import { Box, InputAdornment, Typography } from "@mui/material";
import { MouseEvent, useState } from "react";
import AddEditUserModal from "./add-edit-user-modal";
import Filters from "../../../components/filter/users-filter-popover";
import {
  HeaderRightNode,
  HeaderSearchBox,
  StyledContainedButton,
  StyledTextInput,
} from "./styles";
import { useAppSelector } from "@/store";
import { tokens } from "@/tokens";

const TDClIENTUsers = () => {
  const initialModalState: usersManagementModal = {
    type: "", // "delete", "add", "edit", "restrict", "unblock"
    data: null,
  };
  const initalFilterState: UserFilter = {
    userLevel: "",
    status: "",
  };
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [showAddUserModal, setShowAddUserModal] = useState(initialModalState);
  const [anchorElement, setAnchorElement] = useState<HTMLButtonElement | null>(
    null
  );
  const [filterState, setFilterState] = useState(initalFilterState);
  const [createUser, { isLoading: isCreatingUser }] = useCreateUserMutation();
  const [editUser, { isLoading: isEditingUser }] = useEditUserMutation();
  const [deleteUser, { isLoading: isDeletingUser }] = useDeleteUserMutation();
  const debouncedSearchFn = useDebounce((value: string) => {
    setDebouncedSearch(value);
  }, 1200);
  const { user } = useAppSelector((state) => state.auth);
  const isCurrentUserAdmin = user?.accessLevel === userRightsEnums.admin;
  const { show } = useSnackbar();
  const [updateUserStaus, { isLoading: updateUserStausLoading }] =
    useUpdateUserStatusMutation();
  const {
    data: apiData,
    isLoading,
    isFetching,
  } = useGetAllTdClientsQuery(
    {
      pageNo: page,
      pageSize: limit,
      searchKeyword: debouncedSearch,
      ...(filterState.userLevel && { role: filterState.userLevel }),
      ...(filterState.status && {
        isRestricted: filterState.status !== "active" ? true : false,
      }),
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );
  const { data = [], meta: { countDocs } = { countDocs: 0 } } = apiData ?? {};

  const getErrorMessage = (error: unknown): string => {
    if (error instanceof Error) {
      return error.message;
    }

    if (typeof error === "object" && error !== null && "message" in error) {
      return String((error as { message: unknown }).message);
    }

    return String(error);
  };

  const handleInputChange = (value: string) => {
    setSearch(value);
    debouncedSearchFn(value);
  };

  const handleFilterChange = async (e: UserFilter) => {
    setFilterState(e);
  };

  const handleOpenFilterPopover = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorElement(event.currentTarget);
  };

  const handleSubmit = async (param: IUser) => {
    const addData = {
      userData: {
        firstName: param.firstName,
        lastName: param.lastName,
        email: param.email,
        accessLevel: param.userRight,
        userRole: "TD_CLIENT",
        allowedPages: param.allowedPages,
      },
    };

    const editData = {
      userId: showAddUserModal?.data?._id as string,
      userData: {
        firstName: param.firstName,
        lastName: param.lastName,
        accessLevel: param.userRight,
      },
    };

    try {
      showAddUserModal?.type === "add"
        ? await createUser(addData).unwrap()
        : await editUser(editData).unwrap();
      setShowAddUserModal({
        type: "",
        data: null,
      });
    } catch (error) {
      show(getErrorMessage(error), "", "error");
    }
  };

  const handleEditUser = (obj: usersManagementModal) => {
    setShowAddUserModal({ data: obj.data, type: obj.type });
  };

  const handleDeleteUser = async () => {
    const userId = { userId: showAddUserModal?.data?._id as string };

    try {
      await deleteUser(userId).unwrap();
      setShowAddUserModal((prev) => ({ type: "", data: prev.data }));
    } catch (error) {
      show(getErrorMessage(error), "", "error");
    }
  };

  const columns = useUserColumns(handleEditUser);
  const filterCount = Object.values(filterState).filter(Boolean).length;

  const handleCloseModal = () => {
    setShowAddUserModal(initialModalState);
  };

  const handleUpdateUserStatus = async () => {
    if (showAddUserModal.data) {
      try {
        await updateUserStaus({
          email: showAddUserModal.data.email,
          isRestricted: !showAddUserModal.data.isRestricted,
        }).unwrap();
        show(
          `User ${
            showAddUserModal.data.isRestricted ? "unblocked" : "restricted"
          }!`,
          "",
          "success"
        );
        setShowAddUserModal(initialModalState);
      } catch (error: any) {
        show(error.message, "", "error");
      }
    }
  };

  const name = [showAddUserModal.data?.firstName, showAddUserModal.data?.lastName].filter(Boolean).join(' ');

  return (
    <>
      <PageHeader
        title={"User Management"}
        rightNode={
          <HeaderRightNode gap={3}>
            <HeaderSearchBox>
              <StyledTextInput
                type="text"
                placeholder="Search User"
                value={search}
                sx={{
                  "& .MuiInputBase-input": {
                    paddingLeft: 0,
                  },
                }}
                handleChangeValue={(e) => handleInputChange(e)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <IconImage src={search_img} size="auto" />
                    </InputAdornment>
                  ),
                }}
              />
            </HeaderSearchBox>

            <Box sx={{ position: "relative" }}>
              {filterCount > 0 && (
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    display: "grid",
                    placeContent: "center",
                    backgroundColor: tokens.status.danger,
                    color: "white",
                    borderRadius: "50%",
                    position: "absolute",
                    top: "-10px",
                    right: "-9px",
                    zIndex: "1",
                  }}
                >
                  <Typography variant="caption" fontWeight={tokens.fontWeight.semibold} color="white">
                    {filterCount}
                  </Typography>
                </Box>
              )}

              <StyledContainedButton
                size="large"
                title="Filter"
                variant="outlined"
                style={{ borderColor: tokens.border.default, height: "46px" }}
                startIcon={<IconImage src={black_filter_img} size="auto" />}
                onClick={handleOpenFilterPopover}
              />
            </Box>

            {isCurrentUserAdmin && (
              <StyledContainedButton
                style={{ width: "unset" }}
                startIcon={<IconImage src={plus_img} size="auto" />}
                size="large"
                title="Add User"
                onClick={() => setShowAddUserModal({ type: "add", data: null })}
              />
            )}
          </HeaderRightNode>
        }
      />

      <DynamicTable
        data={data}
        columns={columns}
        isLoading={isLoading || isFetching}
        handlePageChange={setPage}
        handleLimitChange={(e) => {
          setPage(1);
          setLimit(e);
        }}
        limit={limit}
        page={page}
        total={countDocs}
        hidePagination={false}
        key={countDocs}
        stickyHeader
        hideScrollBars
        isFullHeight
        sx={{
          flex: 1,
          // The outer card fills the viewport (isFullHeight), but the table
          // itself should size to its content — otherwise a single data row
          // gets stretched to fill the container. Leftover space below the
          // rows is just the card surface, not stretched rows or fillers.
          "& .MuiTable-root": {
            height: "auto",
            borderCollapse: "collapse",
          },
          "& .MuiTableRow-root:last-of-type .MuiTableCell-root": {
            borderBottom: "1px solid #e6e6e9 !important",
          },
        }}
      />

      <AddEditUserModal
        buttonText={
          isCreatingUser || isEditingUser
            ? "Please Wait"
            : showAddUserModal.data
            ? "Save"
            : "Invite User"
        }
        setShow={() => {
          setShowAddUserModal({ type: "", data: null });
        }}
        key={showAddUserModal.type}
        show={["add", "edit"].includes(showAddUserModal.type)}
        buttonAction={(param) => {
          handleSubmit(param);
        }}
        isEdit={showAddUserModal.data}
        title={showAddUserModal.data ? "Edit Permission" : "Add New User"}
        buttonLoader={isCreatingUser || isEditingUser}
      />

      <Filters
        anchorEl={anchorElement}
        onFiltersChange={handleFilterChange}
        initialFilterState={initalFilterState}
        onClose={() => setAnchorElement(null)}
      />
      <DeleteConfirmModal
        btnLoader={isDeletingUser}
        open={showAddUserModal.type === "delete"}
        itemName={`User “${showAddUserModal?.data?.email}”`}
        subText="user"
        onConfirm={handleDeleteUser}
        maxWidth="xs"
        onClose={() =>
          setShowAddUserModal((prev) => ({ type: "", data: prev.data }))
        }
      />
      {/* Restrict User Modal */}
      {showAddUserModal.type === "restrict" && (
        <RestrictUserModal
          open={showAddUserModal.type === "restrict"}
          name={name}
          onConfirm={handleUpdateUserStatus}
          onClose={handleCloseModal}
          loading={updateUserStausLoading}
          description="user"
        />
      )}
      {/* Unblock User Modal */}
      {showAddUserModal.type === "unblock" && (
        <UnblockUserModal
          open={showAddUserModal.type === "unblock"}
          name={name}
          onConfirm={handleUpdateUserStatus}
          onClose={handleCloseModal}
          loading={updateUserStausLoading}
          description="user"
        />
      )}
    </>
  );
};

export default TDClIENTUsers;
